import React, { useState } from 'react';

/**
 * Issue #408 / #417: Shared confirmation modal that requires the admin to
 * document the reason of an irreversible action (>=20 chars). Used on the
 * Beiträge, Marktplatzobjekte and Kommentare list/detail pages for deletion
 * (#408) and on the user list/detail pages for lock/unlock (#417).
 *
 * Props:
 *   - open:       whether the modal is visible
 *   - title:      modal heading (e.g. "Beitrag löschen")
 *   - description:optional descriptive text shown above the input
 *   - onConfirm(reason): async, called with the trimmed reason on confirm
 *   - onCancel():    called when the admin aborts
 *   - confirmLabel:  label of the confirm button (default "Löschen")
 *   - minLength:     minimum reason length (default 20)
 *   - prompt:        text shown above the textarea (default delete wording)
 *   - placeholder:   textarea placeholder (default delete wording)
 *   - failureFallback: error message when onConfirm throws w/o api message
 */
function DeleteReasonModal({
  open,
  title,
  description,
  onConfirm,
  onCancel,
  confirmLabel = 'Löschen',
  minLength = 20,
  prompt,
  placeholder = 'Grund für die Löschung eingeben...',
  failureFallback = 'Löschen fehlgeschlagen',
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const trimmedLength = reason.trim().length;
  const isValid = trimmedLength >= minLength;

  const reset = () => {
    setReason('');
    setError('');
    setLoading(false);
  };

  const handleCancel = () => {
    reset();
    onCancel && onCancel();
  };

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < minLength) {
      setError(`Der Grund muss mindestens ${minLength} Zeichen lang sein`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(trimmed);
      reset();
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.message || failureFallback;
      setError(apiMessage);
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
        <p>{prompt || `Bitte geben Sie einen Grund für die Löschung an (mindestens ${minLength} Zeichen):`}</p>
        <div className="form-group">
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError('');
            }}
            placeholder={placeholder}
            className="filter-input delete-reason-input"
            rows={4}
            disabled={loading}
          />
          <div className="character-count">
            {trimmedLength}/{minLength} Zeichen
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>
        <div className="form-actions">
          <button
            className="secondary-button"
            onClick={handleCancel}
            disabled={loading}
          >
            Abbrechen
          </button>
          <button
            className="primary-button delete-confirm-button"
            onClick={handleConfirm}
            disabled={loading || !isValid}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteReasonModal;
